import { Position } from '@root/private/js/common/watermark/watermark.interface';
import { checkOverlap } from '@root/private/js/common/watermark/check-overlap';

export function moveWatermark() {
  const { width: videoW, height: videoH } = jwplayer().getVisualQuality().level;
  const watermarkEl = document.querySelectorAll<HTMLDivElement>('.watermark');
  // 워터마크가 실제로 붙어있는 컨테이너를 기준으로 계산해야 함
  // (JW/Flow 모두에서 .player-container와 실제 컨테이너 크기가 다를 수 있음)
  const inferredContainer = ((): HTMLElement | null => {
    const first = watermarkEl[0] as HTMLElement | undefined;
    return (
      (first && (first.offsetParent as HTMLElement)) ||
      (first && first.parentElement) ||
      (jwplayer().getContainer() as HTMLElement) ||
      (document.querySelector('.player-container') as HTMLElement | null)
    );
  })();

  if (!inferredContainer || !videoW || !videoH) return;

  const containerRect = inferredContainer.getBoundingClientRect();
  const cw = containerRect.width;
  const ch = containerRect.height;

  // 실제 영상이 표시되는 영역 계산
  const videoAspect = videoW / videoH;
  const containerAspect = cw / ch;

  let videoRenderW: number;
  let videoRenderH: number;
  let offsetX: number;
  let offsetY: number;

  if (videoAspect > containerAspect) {
    // letterbox (상하 여백)
    videoRenderW = cw;
    videoRenderH = cw / videoAspect;
    offsetX = 0;
    offsetY = (ch - videoRenderH) / 2;
  } else {
    // pillarbox (좌우 여백)
    videoRenderH = ch;
    videoRenderW = ch * videoAspect;
    offsetY = 0;
    offsetX = (cw - videoRenderW) / 2;
  }

  const createdPositions: Position[] = [];

  const tryRandomPlace = (
    el: HTMLDivElement,
    w: number,
    h: number,
    tries = 50,
  ) => {
    // 균일 커버리지용 후보 좌표 생성 + 셔플 + 지터, 이후 완전 랜덤 추가 시도
    const rand01 = () => {
      try {
        const buf = new Uint32Array(1);
        crypto.getRandomValues(buf);
        return buf[0] / 0x100000000;
      } catch {
        return Math.random();
      }
    };

    const innerW = Math.max(0, videoRenderW - w);
    const innerH = Math.max(0, videoRenderH - h);
    if (innerW === 0 || innerH === 0) return false;

    let gridX = Math.max(3, Math.ceil(innerW / Math.max(8, w * 0.8)));
    let gridY = Math.max(3, Math.ceil(innerH / Math.max(8, h * 0.8)));
    let total = gridX * gridY;
    const MAX_CANDIDATES = Math.max(tries, 300);
    if (total > MAX_CANDIDATES) {
      const r = Math.sqrt(total / MAX_CANDIDATES);
      gridX = Math.max(1, Math.floor(gridX / r));
      gridY = Math.max(1, Math.floor(gridY / r));
      total = gridX * gridY;
    }

    const cellW = innerW / gridX;
    const cellH = innerH / gridY;

    const indices = Array.from({ length: total }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand01() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    for (let k = 0; k < indices.length; k += 1) {
      const idx = indices[k];
      const ix = idx % gridX;
      const iy = Math.floor(idx / gridX);
      const left = offsetX + ix * cellW + rand01() * Math.max(0, cellW);
      const top = offsetY + iy * cellH + rand01() * Math.max(0, cellH);

      const newPos: Position = { left, top, width: w, height: h };
      const hasOverlap = createdPositions.some((pos) =>
        checkOverlap(pos, newPos),
      );
      if (!hasOverlap) {
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        createdPositions.push(newPos);
        el.classList.add('show');
        return true;
      }
    }

    for (let i = 0; i < tries; i += 1) {
      const left = offsetX + rand01() * innerW;
      const top = offsetY + rand01() * innerH;
      const newPos: Position = { left, top, width: w, height: h };
      const hasOverlap = createdPositions.some((pos) =>
        checkOverlap(pos, newPos),
      );
      if (!hasOverlap) {
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        createdPositions.push(newPos);
        el.classList.add('show');
        return true;
      }
    }
    return false;
  };

  watermarkEl.forEach((el) => {
    let { width: ww, height: wh } = el.getBoundingClientRect();
    let placed = tryRandomPlace(el, ww, wh, 50);

    if (!placed) {
      if (ww > videoRenderW || wh > videoRenderH) {
        const computed = window.getComputedStyle(el);
        let fontSize = parseFloat(computed.fontSize) || 16;
        const minFont = 8;
        const scale = Math.min(videoRenderW / ww, videoRenderH / wh, 1);
        fontSize = Math.max(minFont, fontSize * scale);
        el.style.fontSize = `${fontSize}px`;
        ({ width: ww, height: wh } = el.getBoundingClientRect());
      }

      const left = offsetX;
      const top = offsetY;
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.classList.add('show');
      createdPositions.push({ left, top, width: ww, height: wh });
    }
  });
}
